import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useFamily } from '../context/FamilyContext';
import {
  subscribeToChores,
  subscribeToDailyCompletions,
} from '@coparent/shared/src/services/choreService';
import { DAYS_OF_WEEK_SHORT } from '@coparent/shared/src/utils/constants';
import { format, subDays, addDays, parseISO } from 'date-fns';
import { HiChevronLeft, HiChevronRight, HiCheckCircle } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import NotificationBell from '../components/notifications/NotificationBell';

function isChoreScheduledForDate(chore, date) {
  const dow = date.getDay();
  if (chore.frequency === 'daily') return true;
  if (chore.frequency === 'specificDays' || chore.frequency === 'weekly') {
    return chore.dayOfWeek?.includes(dow);
  }
  return false;
}

const ChoreHistoryPage = () => {
  const { familyId, childrenMap } = useFamily();
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [chores, setChores] = useState([]);
  const [completions, setCompletions] = useState([]);

  useEffect(() => {
    if (!familyId) return;
    return subscribeToChores(familyId, setChores);
  }, [familyId]);

  useEffect(() => {
    if (!familyId) return;
    return subscribeToDailyCompletions(familyId, selectedDate, setCompletions);
  }, [familyId, selectedDate]);

  const dateObj = useMemo(() => parseISO(selectedDate), [selectedDate]);
  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  const scheduledChores = useMemo(() => {
    return chores.filter((c) => isChoreScheduledForDate(c, dateObj));
  }, [chores, dateObj]);

  const totalTasks = useMemo(() => {
    return scheduledChores.reduce(
      (sum, c) => sum + (c.childIds?.length || 0),
      0
    );
  }, [scheduledChores]);

  const completedCount = completions.length;

  const isCompleted = useCallback(
    (choreId, childId) => {
      return completions.some(
        (c) => c.choreId === choreId && c.childId === childId
      );
    },
    [completions]
  );

  const handlePrevDay = () => {
    setSelectedDate(format(subDays(dateObj, 1), 'yyyy-MM-dd'));
  };

  const handleNextDay = () => {
    const tomorrow = addDays(dateObj, 1);
    if (tomorrow <= new Date()) {
      setSelectedDate(format(tomorrow, 'yyyy-MM-dd'));
    }
  };

  const canGoForward = !isToday;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div>
          <h1 className="page-title">Chore History</h1>
          <p className="page-subtitle">Browse past chore completions</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/chores"
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            Back to Chores
          </Link>
          <NotificationBell />
        </div>
      </div>

      {/* Date navigator */}
      <div className="flex items-center justify-center gap-6 py-4 mb-4 border-b border-surface-100">
        <button
          onClick={handlePrevDay}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-surface-100 transition-colors"
        >
          <HiChevronLeft className="text-xl" />
        </button>
        <div className="text-center min-w-[180px]">
          <p className="font-bold text-ink-900">
            {isToday ? 'Today' : format(dateObj, 'EEEE')}
          </p>
          <p className="text-sm text-ink-400">
            {format(dateObj, 'MMMM d, yyyy')}
          </p>
        </div>
        <button
          onClick={handleNextDay}
          disabled={!canGoForward}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
            canGoForward
              ? 'hover:bg-surface-100'
              : 'opacity-30 cursor-not-allowed'
          }`}
        >
          <HiChevronRight className="text-xl" />
        </button>
      </div>

      {/* Summary */}
      {scheduledChores.length > 0 && totalTasks > 0 && (
        <div className="mb-4">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold ${
              completedCount === totalTasks
                ? 'bg-green-50 text-green-700'
                : 'bg-surface-50 text-ink-400'
            }`}
          >
            <HiCheckCircle
              className={
                completedCount === totalTasks
                  ? 'text-green-500'
                  : 'text-ink-200'
              }
            />
            {completedCount} of {totalTasks} completed
          </span>
        </div>
      )}

      {/* Chore list */}
      {scheduledChores.length === 0 ? (
        <div className="text-center py-16 text-ink-300">
          <p className="text-base font-semibold">No chores scheduled</p>
          <p className="text-sm mt-1">
            No chores were scheduled for {format(dateObj, 'MMMM d')}.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {scheduledChores.map((chore) => (
            <div key={chore.id} className="card-hover p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-ink-900">{chore.name}</h3>
                <div className="flex gap-1.5">
                  {chore.frequency === 'daily' ? (
                    <span className="text-[0.625rem] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide bg-emerald-50 text-emerald-600">
                      Daily
                    </span>
                  ) : (
                    chore.dayOfWeek?.map((d) => (
                      <span
                        key={d}
                        className="text-[0.625rem] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide bg-surface-100 text-ink-400"
                      >
                        {DAYS_OF_WEEK_SHORT[d]}
                      </span>
                    ))
                  )}
                </div>
              </div>

              {chore.childIds?.length > 0 && (
                <div className="border-t border-surface-100 pt-3 space-y-2">
                  {chore.childIds.map((childId) => {
                    const done = isCompleted(chore.id, childId);
                    const child = childrenMap?.[childId];
                    return (
                      <div
                        key={childId}
                        className="flex items-center gap-2.5"
                      >
                        <HiCheckCircle
                          className={`text-lg ${
                            done ? 'text-green-500' : 'text-ink-200'
                          }`}
                        />
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: child?.color || '#d6d3d1',
                          }}
                        />
                        <span
                          className={`text-sm font-medium flex-1 ${
                            done
                              ? 'text-ink-900 line-through'
                              : 'text-ink-400'
                          }`}
                        >
                          {child?.name || ''}
                        </span>
                        <span
                          className={`text-[0.625rem] font-bold uppercase tracking-wide ${
                            done ? 'text-green-600' : 'text-ink-300'
                          }`}
                        >
                          {done ? 'Done' : 'Missed'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChoreHistoryPage;
